import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { FieldData, TextField, useForm, withStyles } from 'vopay';
import { MaterialSymbol } from 'UI';
import { IntegrationSetup } from '../types';
import styles from './useIntegrationSetupFormConfig.module.scss';

const cn = withStyles(styles);

/** Keyed by the API request field each credential maps to (`FlinksUrl`, `GlobalPayAppID`, ...) */
export type IntegrationSetupFormValues = { [fieldName: string]: string };

type UseIntegrationSetupFormConfigArgs = {
  setup: IntegrationSetup;
  /** A connected integration's fields stay disabled until the client confirms they want to edit */
  isDisabled: boolean;
  /** Review step: the fields read back what was entered and can't be changed, but still submit */
  isReviewing: boolean;
  /** Enter inside a field: browsers only submit implicitly with a submit button or a single field, so the drawer decides */
  onEnter: () => void;
  onSubmit: (values: IntegrationSetupFormValues) => Promise<void>;
};

export default function useIntegrationSetupFormConfig({
  setup,
  isDisabled,
  isReviewing,
  onEnter,
  onSubmit,
}: UseIntegrationSetupFormConfigArgs) {
  // Catalogue copy resolves against a root t with full keys (see ../types.ts)
  const { t: tRoot } = useTranslation();

  const config: FieldData<IntegrationSetupFormValues>[] = useMemo(
    () =>
      setup.fields.map((field) => ({
        fieldName: field.name,
        label: () => field.label(tRoot),
        getInputState: () => (isDisabled ? 'disabled' : 'required'),
        showField: () => true,
        // trim + required covers the old "cannot contain only spaces" rule with the localized default message
        validate: () => Yup.string().trim().required().label(field.label(tRoot)),
        renderField: ({ getFormikData }) => (
          <TextField
            variant="outlineShadow"
            preset={field.isSecret ? 'password' : 'text'}
            // Credentials, not a login: keep the browser from filling the user's saved VoPay email + password
            isAutoCompleteDisabled
            elementLeft={<MaterialSymbol icon={field.icon} size="24px" className={cn('fieldIcon')} />}
            placeholder={field.placeholder ?? field.label(tRoot)}
            {...getFormikData<IntegrationSetupFormValues[string]>({})}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onEnter();
              }
            }}
            // Review only disables the input (the field state stays 'required' so the values still submit);
            // the locked state is disabled at the form level too, and must not be undone by this prop
            disabled={isDisabled || isReviewing}
          />
        ),
      })),
    [setup, isDisabled, isReviewing, onEnter, tRoot],
  );

  const initialValues = useMemo<IntegrationSetupFormValues>(
    () => Object.fromEntries(setup.fields.map((field) => [field.name, ''])),
    [setup],
  );

  const { ctx, renderFormElements } = useForm<IntegrationSetupFormValues>({ config, initialValues, onSubmit });

  return { ctx, renderFormElements: () => renderFormElements({ config }) };
}
